import React, { useEffect } from "react"
import api from './api-client'
import { FaCheck, FaExclamationTriangle } from "react-icons/fa"

enum AutomationStatus {
  IDLE = 'IDLE',
  AUTOMATING = 'AUTOMATING',
  COMPLETE = 'COMPLETE',
}

enum AutomationResult {
  SUCCESS = 'SUCCESS',
  MIXED = 'MIXED',
  FAILED = 'FAILED'
}

const IntroduceModeratedCompanies: React.FC = () => {
  const [loading, setLoading] = React.useState(false)
  const [automatingState, setAutomatingState] = React.useState(AutomationStatus.IDLE)
  const [automationDetails, setAutomationDetails] = React.useState<any | null>(null)
  const [selectedDatasourceId, setSelectedDatasourceId] = React.useState<string | null>(null)
  const [datasources, setDatasources] = React.useState<any[]>([])

  const requirements = [
    'Company has a name',
    'Company has a logo url pointing to our storage of logos and can be successfully retrieved from there',
    'Company has a website',
    'Company has at least one tag',
    'Company has a segment'
  ]

  const getDatasources = async () => {
    setLoading(true)
    const response = await api.getCompanyDataUploadsWeCanAutomate()
    setDatasources(response)
    setLoading(false)
  }

  const selectedDatasource = selectedDatasourceId ? datasources.find(ds => ds._id === selectedDatasourceId) : null
  const isAutomating = automatingState === AutomationStatus.AUTOMATING

  const closeModal = () => {
    if (isAutomating) return

    setSelectedDatasourceId(null)
    setAutomatingState(AutomationStatus.IDLE)
  }

  const confirmAutomate = async () => {
    const confirmation = window.confirm(`Are you sure you want to automate approval of ${selectedDatasource.companiesWeCanAutomate.length} companies from ${selectedDatasource.name}?`)
    
    if (confirmation) {
      setAutomatingState(AutomationStatus.AUTOMATING)

      const res = await api.approveCompaniesOfDatasource(
        selectedDatasource._id,
        selectedDatasource.companiesWeCanAutomate.map((co: any) => co._id)
      )

      let status = AutomationResult.SUCCESS
      if (res.failed.length > 0 || res.rejected.length > 0) {
        status = (
          res.failed.length + res.rejected.length === selectedDatasource.companiesWeCanAutomate.length ? 
          AutomationResult.FAILED : AutomationResult.MIXED
        )
      }

      setAutomationDetails({
        status: status,
        symbol: status === AutomationResult.SUCCESS ? <FaCheck color="green" /> : <FaExclamationTriangle color={status == AutomationResult.FAILED ? "red" : "black"} />,
        msg: res.msg,
        failed: res.failed,
        rejected: res.rejected
      })

      setAutomatingState(AutomationStatus.COMPLETE)

      if (status !== AutomationResult.FAILED)
      setDatasources(datasources.filter(ds => ds._id !== selectedDatasource._id))
    }
  }

  useEffect(() => {
    getDatasources()
  }, [])

  return (
    <>
      <button
        type="button"
        className="btn btn-warning"
        onClick={ev => setSelectedDatasourceId('')}
      >
        Add Companies from Moderation by Upload
      </button>
      <div
        className='central-modal'
        style={{display: selectedDatasourceId !== null ? 'flex' : "none"}}
        onPointerDown={closeModal}
      >
        <div
          className='modal-content'
          onPointerDown={(e) => e.stopPropagation()}
          onScroll={(e) => e.stopPropagation()}
        >
          <h1>Automate Company Approval Process</h1>
          <div>
            <p>
              This tool will automatically approve companies from a selected datasource that meet the following criteria:
            </p>
            <ul>
              {requirements.map(req => <li key={req}>{req}</li>)}
            </ul>
            <p>Be sure that you have validated the format of some of the companies in moderation to ensure good formatting of the data being approved.</p>
            <p>Note that you can only automate datasources with more than 15 companies in moderation. Any less and it should be handled manually to ensure best validation.</p>
          </div>
          <div>
          {
            automatingState === AutomationStatus.IDLE ? (
              <>
                <h3>Select the Upload</h3>
                {loading || isAutomating ? (
                  <p>Loading...</p>
                ) : (
                  <select
                    value={selectedDatasourceId || ''}
                    onChange={(e) => setSelectedDatasourceId(e.target.value)}
                    disabled={datasources.length === 0}
                  >
                    <option value={''}>Select a datasource</option>
                    {datasources.map(ds => (
                      <option key={ds._id} value={ds._id}>{ds.name}</option>
                    ))}
                  </select>
                )}
                {!loading && !isAutomating && datasources.length === 0 ? (
                  <p>No datasources eligible for automation.</p>
                ) : null}
                {selectedDatasource && !loading && !isAutomating ? (
                  <div style={{ marginTop: 5, gap: 0 }}>
                    <h4>Summary:</h4>
                    <p><FaCheck color="green" /> <b>Companies that will be automatically approved: {selectedDatasource.companiesWeCanAutomate?.length || 0}</b></p>
                    <div style={{ maxHeight: 100, overflow: 'auto', marginBottom: 16 }}>
                      <ol>
                        {selectedDatasource.companiesWeCanAutomate.map((co: any) => (
                          <li key={co._id}>{co.name}</li>
                        ))}
                      </ol>
                    </div>
                    <p><FaExclamationTriangle color="red" /> <b>Companies unable to be automatically approved: {selectedDatasource.companiesWeCannotAutomate?.length || 0}</b></p>
                    <div style={{ maxHeight: 100, overflow: 'auto', marginBottom: 16 }}>
                      <ol>
                        {selectedDatasource.companiesWeCannotAutomate.map((co: any) => (
                          <li key={co.name || co._id}>{co.name || co._id} - {co.reason}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                ) : null}
              </>
            ) : automatingState === AutomationStatus.AUTOMATING ? (
              <div>
                <h4>Automating...</h4>
                <p>Automating approval of {selectedDatasource.companiesWeCanAutomate.length} companies from {selectedDatasource.name}...</p>
              </div>
            ) : automatingState === AutomationStatus.COMPLETE ? (
              <div>
                <h4>Automation Complete</h4>
                <p>Status: {automationDetails.status} {automationDetails.symbol}</p>
                {automationDetails.msg ? <p>Message: {automationDetails.msg}</p> : null}
                {automationDetails.failed.length > 0 ? (
                  <div>
                    <h5>Failed Companies:</h5>
                    <div style={{ maxHeight: 100, overflow: 'auto' }}>
                      <ol>
                        {automationDetails.failed.map((co: any) => (
                          <li key={co._id}>{co.name} - {co.reason}</li>
                        ))}
                      </ol>
                    </div>
                    <h5>Rejected Companies:</h5>
                    <div style={{ maxHeight: 100, overflow: 'auto' }}>
                      <ol>
                        {automationDetails.rejected.map((co: any) => (
                          <li key={co._id}>{co.name} - {co.reason}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null
          }
          </div>
          <div style={{display: "flex", flexDirection: "row", gap: "5em", margin: "2em 0 0 0"}}>
            <button className='btn btn-secondary' disabled={isAutomating} onClick={closeModal}>Close</button>
            <button
              className='btn btn-primary'
              onClick={confirmAutomate}
              disabled={loading || !selectedDatasource}
              style={{display: automatingState !== AutomationStatus.IDLE ? 'none' : 'block'}}
            >
              Automate Upload
            </button>
          </div>
        </div>
      </div>
    </>
    
  )
}

export default IntroduceModeratedCompanies